/**
 * Detail topicu `/topics/:slug` (S11 → F4AI-18/S12) — `GET /topics/:slug`
 * vracia `{ topic, children, threads, thread_count, limit, issues }`:
 * uzol stromu + deti + posledné N koreňových vlákien (1020/1021/1022 s `a`
 * tagom na adresu topicu). Stránka z toho postaví hlavičku, strom detí a
 * zoznam vlákien s náhľadom (title + prvý riadok textu z `EventCard`).
 *
 * S12 pridáva:
 *  - **composer otázky** (`mode="question"`): kind 1020 s `a` tagom na
 *    adresu topicu + `t` tagom na slug → `POST /events`; po úspešnom
 *    publishi refetch (vlákno pribudne do zoznamu)
 *  - **live update**: `useLiveFeed({ topicAddress })` — nová otázka v
 *    topicu → invalidácia + refetch bez reload-u stránky
 *  - empty/error stavy ponechané (prázdny topic, 404 `topic not found`)
 */
import { type JSX } from 'react';
import { Link, useParams } from 'react-router';

import { useApi } from '../api/ApiContext.js';
import { useResource } from '../hooks/useResource.js';
import { useLiveFeed } from '../hooks/useLiveFeed.js';
import { Composer } from '../components/Composer.js';
import { EventCard } from '../components/EventCard.js';
import { StateEmpty, StateError, StateLoading } from '../components/StateView.js';
import { titleFromContent } from '../lib/format.js';

export function TopicPage(): JSX.Element {
  const { slug = '' } = useParams();
  const api = useApi();
  const topic = useResource(() => api.topic(slug, { limit: 20 }), [api, slug]);

  // live: nová otázka (1020) v tomto topicu → refetch zoznamu vlákien
  const live = useLiveFeed({
    topicSlug: slug,
    topicAddress: topic.data?.topic.address ?? null,
    kinds: [1020],
    onEvent: () => {
      topic.reload();
    },
  });

  return (
    <>
      <nav className="breadcrumbs" aria-label="breadcrumbs">
        <Link to="/">feed</Link>
        <span>/</span>
        <span>topics</span>
        <span>/</span>
        <span>{slug}</span>
      </nav>

      {topic.loading && <StateLoading label={`načítavam topic ${slug}`} />}
      {topic.error !== null && <StateError error={topic.error} onRetry={topic.reload} />}

      {topic.data !== null && (
        <>
          <div className="page-head">
            <div>
              <h1 className="page-title">{topic.data.topic.slug}</h1>
              <p className="page-subtitle">{titleFromContent(topic.data.topic.content, '(popis topicu chýba)')}</p>
              <p className="page-subtitle">
                <span data-testid="topic-live-status" title="živé vlákna cez WS /ws">
                  live: {live.status}
                </span>
              </p>
            </div>
            <p className="untrusted-note">{String(topic.data.thread_count)} vlákien v topicu</p>
          </div>

          {topic.data.children.length > 0 && (
            <section className="glass-panel" style={{ padding: 20 }} aria-label="deti topicu">
              <p className="side-title">deti topicu</p>
              <ul className="topic-tree">
                {topic.data.children.map((child) => (
                  <li key={child.address ?? child.slug}>
                    <Link className="topic-link" to={`/topics/${child.slug}`} data-testid={`topic-child-${child.slug}`}>
                      {child.slug}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {topic.data.threads.length === 0 ? (
            <StateEmpty title="v topicu zatiaľ nie je vlákno" hint="použi composer nižšie — otázka sa viaže `a` tagom na adresu tohto topicu" />
          ) : (
            <div className="stack" data-testid="topic-threads">
              {topic.data.threads.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          )}

          {topic.data.issues.length > 0 && (
            <p className="notice">diagnostika stromu: {topic.data.issues.join('; ')}</p>
          )}

          {topic.data.topic.address !== null && (
            <Composer
              mode="question"
              topic={{ address: topic.data.topic.address, slug: topic.data.topic.slug }}
              heading="polož otázku v tomto topicu"
              testId="topic-composer"
              onPublished={() => {
                topic.reload();
              }}
            />
          )}
        </>
      )}
    </>
  );
}
